<template>
  <!-- 页面主容器 -->
  <div class="withdrawal-container page-content">
    <!-- 标题栏 -->
    <CustomTitleBar title="提现" />

    <!-- 提现金额区域 -->
    <div class="amount-card">
      <!-- 金额输入头部 -->
      <div class="amount-header">
        <span>提现鲸油</span>
        <svgIcon name="qa" color="#0256FF" @click="showFeeInfoModal = true" />
      </div>

      <!-- 金额输入区域 -->
      <div class="amount-input">
        <!-- <span class="currency">¥</span> -->
        <input type="number" v-model="amount" placeholder="0.0" />
        <div class="withdraw-all" @click="withdrawAll">全部提现</div>
      </div>

      <!-- 费用说明 -->
      <div class="fee-info">提现服务费为3.6%，最低提现鲸油100</div>

      <!-- 银行卡选择 -->
      <div class="bank-card-section">
        <div class="section-title">提现到</div>
        <div class="bank-select">
          <div class="selected-bank" v-if="cardInfo.bankName" @click="showDrawer = true">
            <span>{{ cardInfo.bankName }} ({{ cardInfo.bankAccountNo }})</span>
            <svgIcon name="arrow_right" size="10" />
          </div>
          <div v-else class="add-bank" @click="showDrawer = true">选择银行卡</div>
        </div>
      </div>
    </div>

    <!-- 提现按钮区域 -->
    <div class="submit-section">
      <button class="submit-btn" :disabled="!canSubmit" @click="handleSubmit">
        提现
      </button>
      <div class="agreement">
        提现即代表同意<span class="link">《鲸宝支付协议》</span>
      </div>
    </div>

    <!-- 底部说明 -->
    <div class="bottom-tips">
      <div class="contact">
        如有疑问联系鲸宝：<span class="service">客服</span>
      </div>
    </div>

    <!-- 银行卡选择抽屉 -->
    <van-popup v-model:show="showDrawer" position="bottom" round :style="{ height: '60%' }">
      <!-- 银行卡列表抽屉内容 -->
      <div class="drawer-content">
        <div class="drawer-header">
          <span>选择银行卡</span>
          <span class="close-icon" @click="showDrawer = false">
            <svgIcon name="close" size="20" />
          </span>
        </div>
        <div class="bank-list">
          <div v-for="card in cardList" :key="card.id" class="bank-item" @click="selectBank(card)">
            <div class="bank-info">
              <svgIcon name="bankcard" size="20" />
              <div class="bank-detail">
                <div class="bank-name">{{ card.bankName }}</div>
                <div class="card-no">{{ card.bankAccountNo }}</div>
              </div>
            </div>
            <svgIcon :name="cardInfo.linkId === card.linkId ? 'selected' : 'select'" size="20" />
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 费用说明弹窗 -->
    <van-dialog v-model:show="showFeeInfoModal" title="提现规则" :show-cancel-button="false" theme="round-button"
      confirm-button-color="#0256FF" @confirm="showFeeInfoModal = false">
      <div class="fee-info-content">
        <span>1. 单日最高提现次数为 3 次</span>
        <span>2. 单笔最大额度 20000 鲸油，单日累计最大提现金额为 50000 鲸油</span>
        <span>3. 提现费率为 3.6%，请您管理好个人提现次数及金额，以免提现超出限制影响您的使用。</span>
      </div>
    </van-dialog>

    <!-- 支付密码输入抽屉 -->
    <van-popup v-model:show="showPayPasswordDrawer" position="bottom"
      :style="{ height: '60%', borderRadius: '16px 16px 0 0' }" round>
      <div class="password-drawer-container">
        <!-- 抽屉头部 -->
        <div class="drawer-header">
          <van-icon name="cross" size="20" class="close-icon" @click="showPayPasswordDrawer = false" />
          <span class="drawer-title">输入支付密码</span>
          <div class="placeholder"></div>
        </div>

        <!-- 提现金额信息 -->
        <div class="withdrawal-amount-info">
          <div class="amount-label">提现</div>
          <div class="amount-value">¥ {{ amount }}</div>
        </div>

        <!-- 密码输入区域 -->
        <div class="password-input-content">
          <van-password-input :value="payPassword" :length="6" gutter="10px" mask />
          <van-number-keyboard v-model="payPassword" :show="true" type="number" extra-key="" close-button-text="完成" />
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import CustomTitleBar from "@/components/CustomTitleBar/index.vue";
import { getCardList, withdraw } from '@/api/wallet';
import { showToast } from 'vant';

// 响应式状态
const amount = ref('');
const cardList = ref<any[]>([]);
const cardInfo = ref<any>({});
const showDrawer = ref(false);
const showFeeInfoModal = ref(false);
const payPassword = ref('');
const showPayPasswordDrawer = ref(false);

// 计算属性：是否可以提交
const canSubmit = computed(() => {
  return Number(amount.value) >= 100 && !!cardInfo.value.linkId;
});

// 全部提现方法
const withdrawAll = () => {
  // TODO: 实现全部提现逻辑
};

// 选择银行卡
const selectBank = (card: any) => {
  cardInfo.value = {
    linkId: card.linkId,
    bankName: card.bankName,
    bankAccountNo: card.bankAccountNo.slice(-4)
  }
  showDrawer.value = false;
}

// 获取银行卡列表
const getList = async () => {
  const res = await getCardList();
  if (res.code === 200) {
    cardList.value = res.data
    if (cardList.value.length > 0) {
      cardInfo.value = {
        linkId: cardList.value[0].linkId,
        bankName: cardList.value[0].bankName,
        bankAccountNo: cardList.value[0].bankAccountNo.slice(-4)
      }
    } else {
      cardInfo.value = {
        linkId: null,
        bankName: null,
        bankAccountNo: null
      }
    }
  }
};

// 提交提现申请
const handleSubmit = async () => {
  if (!amount.value) {
    showToast('请输入提现金额');
    return;
  }
  if (!cardInfo.value.linkId) {
    showToast('请选择银行卡');
    return;
  }

  // 显示密码输入抽屉
  showPayPasswordDrawer.value = true;
  payPassword.value = ''; // 重置密码
};

// 监听密码变化
watch(payPassword, (newValue) => {
  if (newValue.length === 6) {
    handlePasswordComplete();
  }
});

// 处理密码完成
const handlePasswordComplete = async () => {
  try {
    const res = await withdraw({
      amount: Number(amount.value),
      linkId: cardInfo.value.linkId,
      payPassword: payPassword.value
    });

    if (res.code === 200) {
      //打开第三方页面
      window.open(res.data.pageUrl, '_blank');
      // showToast('提现成功');
      showPayPasswordDrawer.value = false;
      amount.value = '';  // 清空金额
      payPassword.value = '';  // 清空密码
    } else {
      showToast(res.msg);
      payPassword.value = '';  // 清空密码以便重新输入
    }
  } catch (error) {
    showToast('提现失败，请重试');
    payPassword.value = '';  // 清空密码以便重新输入
  }
};

// 组件挂载时获取银行卡列表
onMounted(() => {
  getList()
});
</script>

<style lang="scss" scoped>
// 基础容器样式
.withdrawal-container {
  min-height: 100vh;
  background-color: #f5f5f5;

  &.page-content {
    padding-top: 44px;
  }
}

// 金额卡片样式
.amount-card {
  margin: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;

  // 金额头部
  .amount-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  // 金额输入区域
  .amount-input {
    margin-top: 16px;
    display: flex;
    align-items: center;
    font-size: 24px;

    .currency {
      margin-right: 8px;
      font-weight: 600;
    }

    input {
      border: none;
      outline: none;
      font-size: 24px;
      width: 100%;
    }

    .withdraw-all {
      width: 100px;
      font-size: 14px;
      font-weight: 500;
      color: #333;
      text-align: right;
    }
  }

  // 费用信息
  .fee-info {
    margin-top: 16px;
    color: #999;
    font-size: 14px;
  }

  // 银行卡选择区域
  .bank-card-section {
    margin-top: 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .bank-select {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #666;

      .selected-bank,
      .add-bank {
        display: flex;
        align-items: center;
        color: #333;
        font-size: 14px;

        :deep(svg) {
          margin-left: 8px;
        }
      }

      .add-bank {
        color: #767676;
      }
    }
  }
}

// 提交按钮区域
.submit-section {
  margin: 24px 16px;

  .submit-btn {
    width: 100%;
    padding: 12px;
    background: $primary-color;
    color: white;
    border: none;
    border-radius: 24px;
    font-size: 16px;

    &:disabled {
      background: #ccc;
    }
  }

  .agreement {
    margin-top: 12px;
    margin-left: 20px;
    color: #666;

    .link {
      color: $primary-color;
    }
  }
}

// 底部提示
.bottom-tips {
  position: fixed;
  bottom: 32px;
  left: 0;
  right: 0;
  text-align: center;
  color: #666;
  font-size: 14px;

  .contact {
    margin-bottom: 8px;

    .service {
      color: #0066ff;
    }
  }
}

// 银行卡抽屉样式
.drawer-content {
  padding: 16px;

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid #f5f5f5;

    .close-icon {
      padding: 4px;
    }
  }

  .bank-list {
    .bank-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 0;
      border-bottom: 1px solid #f5f5f5;

      .bank-info {
        display: flex;
        align-items: center;

        .bank-detail {
          margin-left: 12px;

          .bank-name {
            font-size: 16px;
            color: #333;
          }

          .card-no {
            font-size: 14px;
            color: #666;
            margin-top: 4px;
          }
        }
      }
    }
  }
}

// 费用信息弹窗
.fee-info-content {
  padding: 16px;
  line-height: 1.5;
  color: #333;
  font-size: 14px;
  display: flex;
  flex-direction: column;
}

// 密码输入抽屉
.password-drawer-container {
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;

  // 抽屉头部
  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;

    .close-icon {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }

    .drawer-title {
      flex-grow: 1;
      text-align: center;
      font-weight: 500;
      font-size: 14px;
      color: #333333;
    }

    .placeholder {
      width: 20px;
    }
  }

  // 密码输入内容
  .password-input-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    :deep(.van-password-input) {
      width: 100%;

      .van-password-input__security {
        padding: 0 20px;
        box-sizing: border-box;
        justify-content: space-between;

        .van-password-input__item {
          width: 40px;
          height: 40px;
          background: #E5E5E5;
          border-radius: 8px;
          flex: none;
        }
      }
    }
  }

  // 提现金额信息
  .withdrawal-amount-info {
    margin-top: 30px;
    margin-bottom: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .amount-label {
      margin-bottom: 4px;
      font-weight: 400;
      font-size: 15px;
      color: #333333;
    }

    .amount-value {
      font-weight: 500;
      font-size: 24px;
      color: #000000;
    }
  }
}
</style>
